import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Send, Twitter, Mail, Search, Package, ShieldCheck, ArrowRight, MessageCircle } from "lucide-react";
import { fetchSocials } from "@/lib/api";

const FAQ = [
    { q: "How long does shipping take?", a: "Canadian orders ship from our Toronto warehouse and arrive in 5 to 9 business days via Canada Post Tracked. US orders ship cross-border via Tracked Packet (7 to 14 business days)." },
    { q: "Is the shipping discreet?", a: "Yes. Every parcel goes out in plain unmarked packaging with no Helixora branding on the outside. The customs description reads as 'Research Supplies'. There's no signature requirement and the return address is a generic fulfillment label." },
    { q: "Can I track my order?", a: "Yes, once your label is generated we email you a tracking link. You can also self-serve at /track using your order number (from your confirmation email) plus the email you checked out with." },
    { q: "What's your return / refund policy?", a: "Because we sell research-grade lyophilized peptides, opened or reconstituted vials cannot be returned. Unopened vials in original sealed packaging may be returned within 14 days for a refund minus shipping. Damaged-in-transit replacements are free, message us on Telegram with photos." },
    { q: "Do you ship internationally?", a: "We currently ship to Canada and the USA only, dispatched from our Toronto warehouse. We are not shipping to the EU, UK, Australia, or other regions at this time. Sign up to our Telegram channel for updates." },
    { q: "Are your peptides third-party tested?", a: "Yes, every batch is independently tested by Janoshik Analytical (HPLC + mass spec). The COA for each product is linked from the product page via the 'View Certificate of Analysis' button." },
];

export default function Support() {
    const [socials, setSocials] = useState({});
    useEffect(() => { fetchSocials().then(setSocials).catch(() => {}); }, []);

    return (
        <div className="bg-ink-base text-ink min-h-screen" data-testid="support-page">
            {/* HERO */}
            <section className="relative overflow-hidden border-b border-ink">
                <div className="absolute inset-0 grid-lines opacity-25" />
                <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-neon/[0.06] blur-3xl pointer-events-none" />
                <div className="relative max-w-[1280px] mx-auto px-6 sm:px-8 md:px-12 py-20 sm:py-24">
                    <div className="flex items-center gap-4 mb-6">
                        <span className="h-px w-16 bg-neon" />
                        <p className="font-mono text-[10px] tracking-[0.3em] text-neon">/ HUMAN SUPPORT · 24/7 TELEGRAM</p>
                    </div>
                    <h1 className="font-display text-5xl sm:text-7xl lg:text-[88px] font-bold tracking-tight leading-[0.92]">
                        We answer<br />
                        <span className="text-neon">on Telegram.</span>
                    </h1>
                    <p className="mt-8 text-base sm:text-lg text-ink-dim leading-relaxed max-w-2xl">
                        Real human, no chatbot. Ask about reconstitution, shipping ETAs, COA verification, or which peptide fits your protocol. Most messages are answered within an hour.
                    </p>

                    <div className="mt-10 flex flex-wrap gap-3" data-testid="support-channels">
                        {socials.telegram && (
                            <a href={socials.telegram} target="_blank" rel="noopener noreferrer"
                                className="inline-flex items-center gap-3 bg-neon hover:bg-neon-glow text-black px-7 py-4 font-bold text-sm tracking-wide uppercase transition-colors"
                                data-testid="support-telegram-cta">
                                <Send className="w-4 h-4" /> Message us on Telegram <ArrowRight className="w-4 h-4" />
                            </a>
                        )}
                        {socials.twitter && (
                            <a href={socials.twitter} target="_blank" rel="noopener noreferrer"
                                className="inline-flex items-center gap-3 border border-ink-strong hover:border-neon text-ink-dim hover:text-neon px-6 py-4 font-bold text-xs tracking-wide uppercase transition-colors"
                                data-testid="support-twitter-cta">
                                <Twitter className="w-3.5 h-3.5" /> @HPeptides
                            </a>
                        )}
                        <a href="mailto:orders@helixorapeptides.com" className="inline-flex items-center gap-3 border border-ink-strong hover:border-gold text-ink-dim hover:text-gold px-6 py-4 font-bold text-xs tracking-wide uppercase transition-colors" data-testid="support-email-cta">
                            <Mail className="w-3.5 h-3.5" /> orders@helixorapeptides.com
                        </a>
                    </div>
                </div>
            </section>

            {/* QUICK ACTIONS */}
            <section className="border-b border-ink">
                <div className="max-w-[1280px] mx-auto px-6 sm:px-8 md:px-12 py-16 grid sm:grid-cols-3 gap-px bg-ink-strong">
                    <Link to="/track" className="group bg-ink-base p-7 hover:bg-ink-surface transition-colors" data-testid="support-track-link">
                        <Search className="w-5 h-5 text-neon mb-4" />
                        <h3 className="font-display text-xl font-bold text-ink">Track my order</h3>
                        <p className="mt-2 text-sm text-ink-dim leading-relaxed">Enter your order # + email and see live shipment status. No login required.</p>
                        <p className="mt-4 overline text-neon group-hover:text-neon-glow transition-colors">OPEN TRACKER →</p>
                    </Link>
                    <Link to="/research" className="group bg-ink-base p-7 hover:bg-ink-surface transition-colors">
                        <ShieldCheck className="w-5 h-5 text-neon mb-4" />
                        <h3 className="font-display text-xl font-bold text-ink">Research guidelines</h3>
                        <p className="mt-2 text-sm text-ink-dim leading-relaxed">Reconstitution, storage, dosing precision, and our compliance posture in plain English.</p>
                        <p className="mt-4 overline text-neon group-hover:text-neon-glow transition-colors">READ GUIDE →</p>
                    </Link>
                    <a href="https://janoshik.com/" target="_blank" rel="noopener noreferrer" className="group bg-ink-base p-7 hover:bg-ink-surface transition-colors">
                        <Package className="w-5 h-5 text-neon mb-4" />
                        <h3 className="font-display text-xl font-bold text-ink">Verify a COA</h3>
                        <p className="mt-2 text-sm text-ink-dim leading-relaxed">Cross-check any Janoshik analytical report directly on their public verification portal.</p>
                        <p className="mt-4 overline text-neon group-hover:text-neon-glow transition-colors">JANOSHIK.COM →</p>
                    </a>
                </div>
            </section>

            {/* FAQ */}
            <section className="max-w-3xl mx-auto px-6 sm:px-8 md:px-12 py-20">
                <div className="flex items-center gap-3 mb-8">
                    <span className="h-px w-12 bg-gold" />
                    <p className="overline text-gold">/ FREQUENTLY ASKED</p>
                </div>
                <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight leading-[0.96]">Quick answers.</h2>
                <p className="mt-4 text-ink-dim max-w-xl">Couldn't find what you need? Telegram us — we typically reply within the hour.</p>

                <div className="mt-12 divide-y divide-ink-strong border-t border-ink-strong" data-testid="support-faq">
                    {FAQ.map((f, i) => (
                        <details key={i} className="group py-6">
                            <summary className="flex items-center justify-between cursor-pointer list-none">
                                <span className="font-display text-lg font-semibold text-ink pr-4">{f.q}</span>
                                <span className="text-neon font-mono text-2xl group-open:rotate-45 transition-transform shrink-0">+</span>
                            </summary>
                            <p className="mt-4 text-sm text-ink-dim leading-relaxed">{f.a}</p>
                        </details>
                    ))}
                </div>

                {socials.telegram && (
                    <div className="mt-16 p-8 border-l-2 border-neon bg-neon/[0.04]">
                        <MessageCircle className="w-6 h-6 text-neon mb-4" />
                        <p className="font-display text-2xl font-bold text-ink leading-tight">Still have a question?</p>
                        <p className="mt-3 text-sm text-ink-dim leading-relaxed">Drop us a Telegram message — we read every one. Most replies are within an hour during waking hours (NA timezones).</p>
                        <a href={socials.telegram} target="_blank" rel="noopener noreferrer"
                            className="mt-6 inline-flex items-center gap-2 bg-neon hover:bg-neon-glow text-black px-6 py-3 font-bold text-xs tracking-wide uppercase">
                            <Send className="w-3.5 h-3.5" /> Open Telegram chat
                        </a>
                    </div>
                )}
            </section>
        </div>
    );
}
